import { useRef, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import FloatingInput from '../components/FloatingInput';
import PasswordRequirementsHints, {
  getPasswordError,
} from '../components/PasswordRequirementsHints';
import PhoneInputWithCountry from '../components/PhoneInputWithCountry';
import Email from '../components/icons/Email';
import EyeOpen from '../components/icons/EyeOpen';
import { useForm } from '../hooks/useForm';
import LogoBlack from '../assets/img/MOSIDA-black.png';
import QrTicketBtn from '../assets/img/qr-ticket-btn.png';
import ShopNoticeModal from '../shop/ShopNoticeModal';
import LegalModal from './LegalModal';
import {
  allowOnlyOtpDigits,
  forgotPassword,
  isPhoneValidForOtp,
  isValidEmail,
  loginUser,
  loginWithPhone,
  normalizePhoneNumber,
  preSignupCheckPhone,
  registerUser,
  sendPhoneVerification,
  verifyPhoneVerification,
  verifyUserEmail,
} from '../services/auth';
import '../shop/shop.css';

const AUTH_NOTICE_MS = 3000;

function AuthSubmitButton({
  loading = false,
  children,
  type = "submit",
  className = "",
  disabled = false,
  ...rest
}) {
  return (
    <button
      type={type}
      className={`btnPrimary w-100 sqaure${loading ? " btnPrimary--loading" : ""}${
        className ? ` ${className}` : ""
      }`}
      disabled={loading || disabled}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <>
          <span className="btnPrimary__spinner" aria-hidden="true" />
          Loading...
        </>
      ) : (
        children
      )}
    </button>
  );
}

/**
 * Full-width auth card; form content in Col sm={9} md={9} lg={6}.
 * Wired to MOSIDA auth APIs.
 */
function SignInCard() {
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const [view, setView] = useState('signin');
  const [flow, setFlow] = useState({ email: '', userId: '' });
  const [authNotice, setAuthNotice] = useState({
    show: false,
    variant: 'success',
    message: '',
  });

  const go = (next) => {
    setView(next);
    // Replay slide without remounting the card (avoids re-fetching assets).
    const el = cardRef.current;
    if (!el) return;
    el.classList.remove('authCard--viewEnter');
    void el.offsetWidth;
    el.classList.add('authCard--viewEnter');
  };

  const onAuthenticated = () => {
    setAuthNotice({
      show: true,
      variant: 'success',
      message: 'LOGIN SUCCESSFULLY!',
    });
  };

  const onAuthFailed = (message) => {
    setAuthNotice({
      show: true,
      variant: 'failure',
      message: message || 'LOGIN FAILED!',
    });
  };

  const handleAuthNoticeHide = () => {
    const wasSuccess = authNotice.variant === 'success';
    setAuthNotice({ show: false, variant: 'success', message: '' });
    if (wasSuccess) navigate('/my-gallery');
  };

  return (
    <>
      <div
        ref={cardRef}
        className={`authCard mosidaModal authCard--viewEnter ${
          view === 'signup' ? 'signupModal' : 'signinModal'
        }`}
      >
        <div className="authCard__body">
          <Row className="g-0 justify-content-center w-100">
            <Col sm={9} md={9}>
              {view === 'signin' && (
                <SignInView
                  onSwitchToSignup={() => go('signup')}
                  onSwitchToForgot={() => go('forgot')}
                  onAuthenticated={onAuthenticated}
                  onAuthFailed={onAuthFailed}
                />
              )}
              {view === 'signup' && (
                <SignUpView
                  onSwitchToSignin={() => go('signin')}
                  onContinue={(email) => {
                    setFlow((prev) => ({ ...prev, email }));
                    go('emailVerify');
                  }}
                />
              )}
              {view === 'emailVerify' && (
                <EmailVerifyView
                  email={flow.email}
                  onComplete={() => {
                    toast.success('Email verified. Please sign in.');
                    go('signin');
                  }}
                />
              )}
              {view === 'forgot' && (
                <ForgotView
                  onSwitchToSignup={() => go('signup')}
                  onContinue={(email) => {
                    setFlow((prev) => ({ ...prev, email }));
                    go('resetLinkSent');
                  }}
                />
              )}
              {view === 'resetLinkSent' && (
                <ResetLinkSentView onContinue={() => go('signin')} />
              )}
              {view === 'success' && (
                <SuccessView onContinue={() => go('signin')} />
              )}
            </Col>
          </Row>
        </div>
      </div>

      <ShopNoticeModal
        show={authNotice.show}
        variant={authNotice.variant}
        message={authNotice.message}
        duration={AUTH_NOTICE_MS}
        dismissible={false}
        onHide={handleAuthNoticeHide}
      />
    </>
  );
}

function AuthLogo() {
  return (
    <div className="authLogo">
      <img className="pointer authLogo__img" src={LogoBlack} alt="MOSIDA" />
    </div>
  );
}

function SignInView({
  onSwitchToSignup,
  onSwitchToForgot,
  onAuthenticated,
  onAuthFailed,
}) {
  const navigate = useNavigate();
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { values, handleChange, setValues } = useForm({
    email: '',
    password: '',
    phoneNumber: '',
    verificationCode: '',
  });
  const [showPhoneStep, setShowPhoneStep] = useState(true);
  const [pendingPhone, setPendingPhone] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!values.email?.trim()) return toast.error('Email is required.');
    if (!isValidEmail(values.email)) return toast.error('Please enter a valid email.');
    if (!values.password?.trim()) return toast.error('Password is required.');
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await loginUser({
        email: values.email.trim().toLowerCase(),
        password: values.password,
      });
      onAuthenticated();
    } catch (error) {
      onAuthFailed?.(error?.message || 'LOGIN FAILED!');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendOTP = async () => {
    const normalized = normalizePhoneNumber(values.phoneNumber);
    if (!isPhoneValidForOtp(normalized)) {
      return toast.error('Enter a valid phone number with at least 10 digits.');
    }
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await sendPhoneVerification({ phone_number: normalized, purpose: 'login' });
      setPendingPhone(normalized);
      setShowPhoneStep(false);
      setValues((prev) => ({ ...prev, verificationCode: '' }));
      toast.success('Verification code sent.');
    } catch (error) {
      onAuthFailed?.(error?.message || 'LOGIN FAILED!');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    if (!pendingPhone || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await sendPhoneVerification({ phone_number: pendingPhone, purpose: 'login' });
      setValues((prev) => ({ ...prev, verificationCode: '' }));
      toast.success('A new code was sent.');
    } catch (error) {
      onAuthFailed?.(error?.message || 'LOGIN FAILED!');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOTP = async () => {
    const code = allowOnlyOtpDigits(values.verificationCode, 6);
    if (!pendingPhone || code.length < 6) {
      return toast.error('Enter the 6-digit code.');
    }
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await verifyPhoneVerification({ phone_number: pendingPhone, code });
      await loginWithPhone({ phone_number: pendingPhone });
      onAuthenticated();
    } catch (error) {
      onAuthFailed?.(error?.message || 'LOGIN FAILED!');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <AuthLogo />
      <div className="sectionHeading mb-4 pb-2">
        Sign in to view your
        <br />
        Gallery and Orders
      </div>
      <Form onSubmit={handleSubmit} autoComplete="off">
        <FloatingInput
          placeholder="example@gmail.com"
          label="E-mail"
          icon={Email}
          type="text"
          name="email"
          value={values.email}
          onChange={handleChange}
          autoComplete="off"
        />
        <FloatingInput
          placeholder="********"
          label="Password"
          icon={EyeOpen}
          type="password"
          name="password"
          value={values.password}
          onChange={handleChange}
          setVisiblePassword={setVisiblePassword}
          visiblePassword={visiblePassword}
          autoComplete="new-password"
        />
        <AuthSubmitButton loading={isSubmitting}>Sign In</AuthSubmitButton>
      </Form>

      <div className="divider">
        <span className="divider-text">or sign in with</span>
      </div>

      <div className="signinPhoneAuth mb-2">
        {showPhoneStep ? (
          <PhoneInputWithCountry
            key="signin-phone-number"
            placeholder="Phone number"
            label="Phone number"
            text="Send OTP"
            name="phoneNumber"
            value={values.phoneNumber}
            onChange={handleChange}
            onSend={handleSendOTP}
            sendDisabled={isSubmitting}
          />
        ) : (
          <>
            <FloatingInput
              key="signin-phone-code"
              placeholder="Enter verification code"
              label="Verify OTP"
              type="tel"
              name="verificationCode"
              value={values.verificationCode}
              onChange={(e) =>
                setValues((prev) => ({
                  ...prev,
                  verificationCode: allowOnlyOtpDigits(e.target.value, 6),
                }))
              }
              text={isSubmitting ? '…' : 'Verify & Sign In'}
              onSend={handleVerifyOTP}
              sendDisabled={isSubmitting}
            />
            <div className="otpActionsRow">
              <button
                type="button"
                className="changePhoneLink"
                onClick={() => {
                  setShowPhoneStep(true);
                  setValues((prev) => ({ ...prev, verificationCode: '' }));
                }}
              >
                Change phone number
              </button>
              <button
                type="button"
                className="resendLink"
                onClick={handleResendOTP}
                disabled={isSubmitting}
              >
                Resend code
              </button>
            </div>
          </>
        )}
      </div>

      <button
        type="button"
        className="btnOutline w-100 sqaure"
        onClick={() => navigate('/scan')}
      >
        <img className="btnOutline__icon" src={QrTicketBtn} alt="" />
        Scan your QR Ticket
      </button>

      <div className="mt-3 pt-md-2">
        <div className="dontHaveAccount">
          Don&apos;t have an account?
          <span className="text-primary" onClick={onSwitchToSignup}>
            Sign up
          </span>
        </div>
        <div className="dontHaveAccount pointer mb-0" onClick={onSwitchToForgot}>
          Forgot password?
        </div>
      </div>
    </>
  );
}

function SignUpView({ onSwitchToSignin, onContinue }) {
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [visibleRePassword, setVisibleRePassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPhoneOtp, setShowPhoneOtp] = useState(false);
  const [pendingPhone, setPendingPhone] = useState('');
  const [pendingSignup, setPendingSignup] = useState(null);
  const [legalModal, setLegalModal] = useState(null); // 'terms' | 'privacy' | null
  const { values, handleChange, setValues } = useForm({
    firstName: '',
    lastName: '',
    username: '',
    phoneNumber: '',
    email: '',
    password: '',
    confirmPassword: '',
    verificationCode: '',
    acceptTerms: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!values.firstName?.trim()) return toast.error('First name is required.');
    if (!values.lastName?.trim()) return toast.error('Last name is required.');
    if (!values.username?.trim()) return toast.error('Username is required.');
    const phone_number = normalizePhoneNumber(values.phoneNumber);
    if (!isPhoneValidForOtp(phone_number)) {
      return toast.error('Enter a valid phone number with at least 10 digits.');
    }
    if (!values.email?.trim()) return toast.error('Email is required.');
    if (!isValidEmail(values.email)) return toast.error('Please enter a valid email.');
    const passwordError = getPasswordError(values.password, values.confirmPassword);
    if (passwordError) return toast.error(passwordError);
    if (!values.acceptTerms) return toast.error('Please accept the terms.');

    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await preSignupCheckPhone({ phone_number });
      await sendPhoneVerification({ phone_number, purpose: 'signup' });
      setPendingSignup({
        email: values.email.trim().toLowerCase(),
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        username: values.username.trim(),
        password: values.password,
        confirmPassword: values.confirmPassword,
        phone_number,
      });
      setPendingPhone(phone_number);
      setValues((prev) => ({ ...prev, verificationCode: '' }));
      setShowPhoneOtp(true);
      toast.success('Verification code sent to your phone.');
    } catch (error) {
      toast.error(error?.message || 'Signup failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendSignupOtp = async () => {
    if (!pendingPhone || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await sendPhoneVerification({ phone_number: pendingPhone, purpose: 'signup' });
      setValues((prev) => ({ ...prev, verificationCode: '' }));
      toast.success('A new code was sent.');
    } catch (error) {
      toast.error(error?.message || 'Could not resend code.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifySignupOtp = async (e) => {
    e.preventDefault();
    const code = allowOnlyOtpDigits(values.verificationCode, 6);
    if (!pendingPhone || code.length < 6) {
      return toast.error('Enter the 6-digit code.');
    }
    if (!pendingSignup) {
      return toast.error('Something went wrong. Please fill the form again.');
    }
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await verifyPhoneVerification({ phone_number: pendingPhone, code });
      await registerUser(pendingSignup);
      toast.success('Account created. Please verify your email.');
      onContinue(pendingSignup.email);
    } catch (error) {
      toast.error(error?.message || 'Signup failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showPhoneOtp) {
    return (
      <>
        <AuthLogo />
        <div className="sectionHeading mb-0 pb-2">Verify your phone</div>
        <p className="text-muted mb-4 pb-2 text-center">
          Enter the 6-digit code sent to {pendingPhone}.
        </p>
        <Form onSubmit={handleVerifySignupOtp}>
          <FloatingInput
            placeholder="Enter verification code"
            label="Verify OTP"
            type="tel"
            name="verificationCode"
            value={values.verificationCode}
            onChange={(e) =>
              setValues((prev) => ({
                ...prev,
                verificationCode: allowOnlyOtpDigits(e.target.value, 6),
              }))
            }
          />
          <div className="text-end small mb-3">
            <button
              type="button"
              className="resendLink"
              onClick={handleResendSignupOtp}
              disabled={isSubmitting}
            >
              Resend code
            </button>
          </div>
          <AuthSubmitButton loading={isSubmitting}>Verify & Sign Up</AuthSubmitButton>
          <button
            type="button"
            className="btnBackLink"
            onClick={() => {
              setShowPhoneOtp(false);
              setValues((prev) => ({ ...prev, verificationCode: '' }));
            }}
          >
            Back to <span className="btnBackLink__signup">Sign Up</span>
          </button>
        </Form>
      </>
    );
  }

  return (
    <>
      <AuthLogo />
      <div className="sectionHeading text-md-center mb-4 pb-2">Sign Up to MOSIDA</div>
      <Form onSubmit={handleSubmit}>
        <Row className="g-2">
          <Col md={6} xs={6}>
            <FloatingInput
              placeholder="Enter first name"
              label="First Name"
              type="text"
              name="firstName"
              value={values.firstName}
              onChange={handleChange}
            />
          </Col>
          <Col md={6} xs={6}>
            <FloatingInput
              placeholder="Enter last name"
              label="Last Name"
              type="text"
              name="lastName"
              value={values.lastName}
              onChange={handleChange}
            />
          </Col>
        </Row>
        <FloatingInput
          placeholder="Enter username"
          label="Username"
          type="text"
          name="username"
          value={values.username}
          onChange={handleChange}
        />
        <div className="signinPhoneAuth mb-2">
          <PhoneInputWithCountry
            placeholder="Phone number"
            label="Phone number"
            name="phoneNumber"
            value={values.phoneNumber}
            onChange={handleChange}
          />
        </div>
        <FloatingInput
          placeholder="example@gmail.com"
          label="E-mail"
          type="text"
          name="email"
          value={values.email}
          onChange={handleChange}
        />
        <FloatingInput
          placeholder="********"
          label="Password"
          icon={EyeOpen}
          type="password"
          name="password"
          value={values.password}
          onChange={handleChange}
          setVisiblePassword={setVisiblePassword}
          visiblePassword={visiblePassword}
        />
        <FloatingInput
          placeholder="********"
          label="Repeat Password"
          icon={EyeOpen}
          type="password"
          name="confirmPassword"
          value={values.confirmPassword}
          onChange={handleChange}
          setVisiblePassword={setVisibleRePassword}
          visiblePassword={visibleRePassword}
        />
        <PasswordRequirementsHints
          password={values.password}
          confirmPassword={values.confirmPassword}
        />
        <div className="form-check mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="flexCheckChecked"
            checked={values.acceptTerms}
            name="acceptTerms"
            onChange={handleChange}
          />
          <label className="form-check-label" htmlFor="flexCheckChecked" style={{ color: 'var(--input-auth)' }}>
            I agree with{' '}
            <button
              type="button"
              className="privacyLink"
              onClick={() => setLegalModal('terms')}
            >
              Terms of Use
            </button>{' '}
            and{' '}
            <button
              type="button"
              className="privacyLink"
              onClick={() => setLegalModal('privacy')}
            >
              Privacy Policy
            </button>
          </label>
        </div>
        <AuthSubmitButton loading={isSubmitting}>Sign Up</AuthSubmitButton>
      </Form>
      <div className="alreadyHaveAccount">
        Already have an account?
        <span className="text-primary" onClick={onSwitchToSignin}>
          Sign in
        </span>
      </div>
      <LegalModal
        show={Boolean(legalModal)}
        type={legalModal || 'terms'}
        onHide={() => setLegalModal(null)}
      />
    </>
  );
}

function EmailVerifyView({ email, onComplete }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { values, handleChange } = useForm({ code: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!values.code?.trim()) return toast.error('Verification code is required.');
    setIsSubmitting(true);
    try {
      await verifyUserEmail({
        email,
        verification_code: String(values.code).trim(),
      });
      toast.success('Code verified successfully');
      onComplete();
    } catch (error) {
      toast.error(error?.message || 'Verification failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <AuthLogo />
      <div className="sectionHeading mb-0 pb-2">Verify your email</div>
      <p className="text-muted mb-4 pb-3">
        Enter the verification code we sent to your email address.
      </p>
      <Form onSubmit={handleSubmit}>
        <FloatingInput
          placeholder="Enter verification code"
          label="Verification code"
          type="text"
          name="code"
          value={values.code}
          onChange={handleChange}
        />
        <AuthSubmitButton loading={isSubmitting}>Complete Sign Up</AuthSubmitButton>
      </Form>
    </>
  );
}

function ForgotView({ onSwitchToSignup, onContinue }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { values, handleChange } = useForm({ email: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!values.email?.trim()) return toast.error('Email is required.');
    if (!isValidEmail(values.email)) return toast.error('Please enter a valid email.');
    setIsSubmitting(true);
    try {
      const email = values.email.trim().toLowerCase();
      await forgotPassword({ email });
      toast.success('Password reset link sent.');
      onContinue(email);
    } catch (error) {
      toast.error(error?.message || 'Failed to send reset link.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <AuthLogo />
      <div className="sectionHeading mb-0 pb-2">Forgot your password?</div>
      <p className="text-muted mb-4 pb-3">
        Enter your email and we&apos;ll send you a password reset link.
      </p>
      <Form onSubmit={handleSubmit}>
        <FloatingInput
          placeholder="example@gmail.com"
          label="E-mail"
          icon={Email}
          type="text"
          name="email"
          value={values.email}
          onChange={handleChange}
        />
        <AuthSubmitButton loading={isSubmitting}>Continue</AuthSubmitButton>
      </Form>
      <div className="dontHaveAccount mt-4">
        Don&apos;t have an account?{' '}
        <span className="text-primary" onClick={onSwitchToSignup}>
          Sign up
        </span>
      </div>
    </>
  );
}

function ResetLinkSentView({ onContinue }) {
  return (
    <div className="authConfirm">
      <AuthLogo />
      <div className="sectionHeading text-center mb-2 pb-2">
        Password reset link sent.
      </div>
      <p className="text-muted text-center mb-4 pb-3">Please check your email.</p>
      <button className="btnPrimary w-100 sqaure" type="button" onClick={onContinue}>
        Continue
      </button>
    </div>
  );
}

function SuccessView({ onContinue }) {
  return (
    <div className="authConfirm">
      <AuthLogo />
      <div className="sectionHeading text-center mb-2 pb-3">Password Changed!</div>
      <p className="text-muted text-center mb-4 pb-3">
        Your password has been changed successfully.
      </p>
      <button className="btnPrimary w-100 sqaure" type="button" onClick={onContinue}>
        Continue
      </button>
    </div>
  );
}

export default SignInCard;
